/**
 * Curated gallery pieces — not every story panel.
 * Prefer hero shots and chapter signatures; skip mid filler art.
 */

export type GalleryGroup = "verse" | "chapter";

export type GalleryUnlock =
  /** Own at least one copy of this verse (default card face). */
  | { type: "own-verse"; verseId: string }
  /** First clear of this mission (any difficulty). */
  | { type: "mission-clear"; missionId: string }
  /** First clear of the chapter finale (any difficulty). */
  | { type: "chapter-finale"; chapterId: string };

export interface GalleryPiece {
  id: string;
  title: string;
  /** Optional short caption under the title. */
  caption?: string;
  group: GalleryGroup;
  /** Path under site root, e.g. /art/cards/heb-11-1.webp */
  src: string;
  unlock: GalleryUnlock;
  /**
   * Campaign hub cover — featured as the section hero.
   * Unlocks when the campaign (chapter) is completed.
   */
  featured?: boolean;
}

export interface GalleryChapterSection {
  id: string;
  title: string;
  /** Short label for progress UI (no "Chapter ·" prefix). */
  shortTitle: string;
  /** Optional kicker under the section title. */
  lead?: string;
  pieces: readonly GalleryPiece[];
}

/** Rebuild the Wall — only standout panels, not the full story set. */
const NEH_CHAPTER_PIECES: GalleryPiece[] = [
  {
    id: "neh-wall-cover",
    title: "Rebuild the Wall",
    caption: "Campaign cover · Nehemiah",
    group: "chapter",
    src: "/art/story/neh-wall/campaign-cover.webp",
    unlock: { type: "chapter-finale", chapterId: "neh-wall" },
    featured: true,
  },
  {
    id: "neh-wall-favor",
    title: "Before the King",
    caption: "Favor in the presence of power",
    group: "chapter",
    src: "/art/story/neh-wall/n1-favor.webp",
    unlock: { type: "mission-clear", missionId: "neh-before-the-king" },
  },
  {
    id: "neh-wall-by-night",
    title: "The Night Survey",
    caption: "Ride the ruin by night",
    group: "chapter",
    src: "/art/story/neh-wall/n2-by-night.webp",
    unlock: { type: "mission-clear", missionId: "neh-night-survey" },
  },
  {
    id: "neh-wall-mind-to-work",
    title: "A Mind to Work",
    caption: "The people had a mind to work",
    group: "chapter",
    src: "/art/story/neh-wall/n4-mind-to-work.webp",
    unlock: { type: "mission-clear", missionId: "neh-mockery" },
  },
  {
    id: "neh-wall-sword-labor",
    title: "Sword and Trowel",
    caption: "Build with one hand; hold the sword with the other",
    group: "chapter",
    src: "/art/story/neh-wall/n6-sword-labor.webp",
    unlock: { type: "mission-clear", missionId: "neh-sword-and-trowel" },
  },
  {
    id: "neh-wall-finished",
    title: "The Wall Finished",
    caption: "Signature · Rebuild the Wall",
    group: "chapter",
    src: "/art/story/neh-wall/n7-finished.webp",
    unlock: { type: "chapter-finale", chapterId: "neh-wall" },
  },
  {
    id: "neh-wall-strength",
    title: "Joy of the Lord",
    caption: "The joy of the Lord is your strength",
    group: "chapter",
    src: "/art/story/neh-wall/n8-strength.webp",
    unlock: { type: "chapter-finale", chapterId: "neh-wall" },
  },
];

/** The Three Hundred — standout Gideon panels only. */
const GID_CHAPTER_PIECES: GalleryPiece[] = [
  {
    id: "gideon-300-cover",
    title: "The Three Hundred",
    caption: "Campaign cover · Judges 6–7",
    group: "chapter",
    src: "/art/story/gideon-300/gideon-campaign-cover.webp",
    unlock: { type: "chapter-finale", chapterId: "gideon-300" },
    featured: true,
  },
  {
    id: "gideon-300-question",
    title: "The Question",
    caption: "If the Lord be with us, why…?",
    group: "chapter",
    src: "/art/story/gideon-300/g1-the-question.webp",
    unlock: { type: "mission-clear", missionId: "gid-winepress" },
  },
  {
    id: "gideon-300-fire",
    title: "Fire from the Rock",
    caption: "Peace be unto thee; fear not",
    group: "chapter",
    src: "/art/story/gideon-300/g2-fire-from-the-rock.webp",
    unlock: { type: "mission-clear", missionId: "gid-jehovah-shalom" },
  },
  {
    id: "gideon-300-night",
    title: "That Same Night",
    caption: "Tear down the altar of Baal",
    group: "chapter",
    src: "/art/story/gideon-300/g3-that-same-night.webp",
    unlock: { type: "mission-clear", missionId: "gid-jerubbaal" },
  },
  {
    id: "gideon-300-trumpet",
    title: "Spirit and Trumpet",
    caption: "The Spirit of the Lord came upon Gideon",
    group: "chapter",
    src: "/art/story/gideon-300/g4-spirit-trumpet.webp",
    unlock: { type: "mission-clear", missionId: "gid-jezreel" },
  },
  {
    id: "gideon-300-water",
    title: "By the Water",
    caption: "By the three hundred will I save you",
    group: "chapter",
    src: "/art/story/gideon-300/g6-by-the-water.webp",
    unlock: { type: "mission-clear", missionId: "gid-three-hundred" },
  },
  {
    id: "gideon-300-pitchers",
    title: "Every Man in His Place",
    caption: "Signature · The Three Hundred",
    group: "chapter",
    src: "/art/story/gideon-300/g7-every-man-in-place.webp",
    unlock: { type: "chapter-finale", chapterId: "gideon-300" },
  },
];

export const GALLERY_CHAPTER_SECTIONS: readonly GalleryChapterSection[] = [
  {
    id: "neh-wall",
    title: "Chapter · Rebuild the Wall",
    shortTitle: "Rebuild the Wall",
    lead: "Nehemiah · Fear · hold the wall",
    pieces: NEH_CHAPTER_PIECES,
  },
  {
    id: "gideon-300",
    title: "Chapter · The Three Hundred",
    shortTitle: "The Three Hundred",
    lead: "Judges 6–7 · Doubt · three hundred",
    pieces: GID_CHAPTER_PIECES,
  },
];

/** Preferred display order for verse category groups. */
export const VERSE_CATEGORY_ORDER = ["faith", "peace", "wisdom"] as const;

export function verseCategoryLabel(category: string): string {
  if (category === "faith") return "Faith";
  if (category === "peace") return "Peace";
  if (category === "wisdom") return "Wisdom";
  return category.charAt(0).toUpperCase() + category.slice(1);
}

/**
 * Verse faces are generated from the deck catalog at runtime so new cards
 * appear without hand-maintaining this list.
 */
export function verseFacePiece(
  verseId: string,
  title: string,
): GalleryPiece {
  return {
    id: `verse:${verseId}`,
    title,
    group: "verse",
    src: `/art/cards/${verseId}.webp`,
    unlock: { type: "own-verse", verseId },
  };
}

export const GALLERY_CHAPTER_PIECES: readonly GalleryPiece[] = [
  ...NEH_CHAPTER_PIECES,
  ...GID_CHAPTER_PIECES,
];

export function galleryPieceById(
  id: string,
  verseTitles: Map<string, string>,
): GalleryPiece | undefined {
  const chapter = GALLERY_CHAPTER_PIECES.find((p) => p.id === id);
  if (chapter) return chapter;
  if (id.startsWith("verse:")) {
    const verseId = id.slice("verse:".length);
    const title = verseTitles.get(verseId) ?? verseId;
    return verseFacePiece(verseId, title);
  }
  return undefined;
}

export function artIdsForOwnVerse(verseId: string): string[] {
  return [`verse:${verseId}`];
}

export function artIdsForMissionClear(missionId: string): string[] {
  return GALLERY_CHAPTER_PIECES.filter(
    (p) =>
      p.unlock.type === "mission-clear" && p.unlock.missionId === missionId,
  ).map((p) => p.id);
}

export function artIdsForChapterFinale(chapterId: string): string[] {
  return GALLERY_CHAPTER_PIECES.filter(
    (p) =>
      p.unlock.type === "chapter-finale" && p.unlock.chapterId === chapterId,
  ).map((p) => p.id);
}
